import React, { useState, useEffect } from 'react'
import { notify } from 'libs/common/notify'

import TextField from 'shared/components/community/components/text-field/TextField'
import EditableImage from 'shared/components/community/components/image-croping/EditableImage'
import CropImageModal from 'shared/components/community/components/image-croping/CropImageModal'
import Button from 'shared/components/community/components/button/Button'
import { FieldError } from '@elo-kit/components/elo-ui/field-error/FieldError'

import { AVATAR_CROP_PARAMS } from 'constants/profile.constants'
import { MAX_USERNAME_LENGTH } from 'constants/communities.constants'

import { withStores } from 'utils/helpers.utils'

import './payer-community-form.scss'

const PayerCommunityForm = (props) => {
  const {
    communitiesStore,
    communityMemberStore,
    communityMembersStore,
    userStore,
    toggle,
    submitButtonChild,
    cancelButtonChild,
    communityId,
    isCommunitySettings,
    courseViewsStore: { setActiveCommunity },
    communityPostsStore,
  } = props
  const { item: member } = communityMemberStore
  const { avatarCropX, avatarCropY, avatarCropW, avatarCropH, avatar: memberAvatar = [] } = member || {}

  const [username, setUsername] = useState('')
  const [editAvatarModal, setEditAvatarModal] = useState(false)
  const [avatar, setAvatar] = useState({})
  const [profileLoading, setProfileLoading] = useState(false)
  const [isUserNameValid, setIsUserNameValid] = useState(true)
  useEffect(() => {
    if (isCommunitySettings) {
      setUsername(member?.username)
      setAvatar({
        x: avatarCropX,
        y: avatarCropY,
        width: avatarCropW,
        height: avatarCropH,
        naturalWidth: avatarCropW,
        naturalHeight: avatarCropH,
      })
    }
  }, [])

  const fetchCommunityData = async () => {
    const communityResp = await communitiesStore.fetchItem(communityId)

    if (communityResp?.success) {
      setActiveCommunity(communityResp.data)
    }
  }

  const handleSubmit = async (callback) => {
    const userNameResp = await communityMemberStore.validateMemberName(
      {
        username: username,
        discussionId: communityId,
      },
      { skipErrorNotific: true }
    )

    setIsUserNameValid(userNameResp.success)

    if (!userNameResp.success) {
      setProfileLoading(false)
      return
    }
    callback()

    const { width, height } = avatar
    const isAvatarNotCropped = (!width || width === 0) && (!height || height === 0)

    const cropData = isAvatarNotCropped
      ? {
          avatarCropX: null,
          avatarCropY: null,
          avatarCropW: avatar.naturalWidth,
          avatarCropH: avatar.naturalHeight,
        }
      : {
          avatarCropX: avatar.x,
          avatarCropY: avatar.y,
          avatarCropW: avatar.width,
          avatarCropH: avatar.height,
        }
    const reqData = {
      ...cropData,
      userId: userStore.item.id,
      communityId,
      username,
      ...(!!avatar.imageUrl && { avatarRemoteUrl: avatar.imageUrl }),
    }

    const submitAction = () =>
      isCommunitySettings
        ? communityMembersStore.updateItem({
            id: member.id,
            ...reqData,
          })
        : communityMembersStore.createItem(reqData)

    setProfileLoading(true)
    const resp = await submitAction()

    if (resp?.success) {
      if (isCommunitySettings) {
        notify('success', I18n.t('react.shared.community.notific.profile_settings_updated'))
        communityPostsStore.resetData()
        await communityMemberStore.fetchItem(communityId)
        communityPostsStore.fetchList({ communityId })
      } else {
        notify('success', I18n.t('react.shared.community.notific.joined_to_community'))
        await communityMemberStore.fetchItem(communityId)
        fetchCommunityData()
      }
      setProfileLoading(false)
    } else {
      setProfileLoading(false)
    }
  }

  const usernameLength = username?.trim().length
  const avatarLink = memberAvatar[0]?.cropped || memberAvatar[0]?.original
  const validUsername = usernameLength < MAX_USERNAME_LENGTH

  const submitDisabled = !username?.length || !validUsername || profileLoading

  return (
    <div className='payer-community-form'>
      <div className='payer-community-form__input-container'>
        <div className='payer-community-form--profile-label'>{I18n.t('react.shared.comminity.nickname')}</div>
        {!validUsername && <FieldError errorText={I18n.t('react.shared.community.validations.username_invalid')} />}
        {validUsername && !isUserNameValid && (
          <FieldError errorText={I18n.t('react.shared.comminity.username_exists')} />
        )}
      </div>
      <div className='payer-community-form__field-input'>
        <TextField
          className='payer-community-form-input'
          placeholder={I18n.t('react.shared.comminity.your_community_nickname')}
          value={username}
          onChange={setUsername}
        />
      </div>
      <div className='payer-community-form--profile-label'>{I18n.t('react.shared.comminity.avatar')}</div>
      <div className='payer-community-form--profile-avatar'>
        <EditableImage
          avatar={avatar}
          imageUrl={avatar.imageUrl || avatarLink}
          loadingImage={false}
          onImageEdit={() => setEditAvatarModal(true)}
          imageType='round'
          inForm
          description={I18n.t('react.shared.community.welcome_modal.profile_description')}
          noImageIcon='fas fa-user'
          croppedImage={!!avatar.imageUrl}
          circularProp
        />
      </div>
      {editAvatarModal && (
        <CropImageModal
          cropParams={AVATAR_CROP_PARAMS}
          image={avatar.imageUrl || memberAvatar[0]?.original}
          loaderLabel={I18n.t('react.shared.cover_image')}
          onCrop={(cropDetails, image) => {
            const imageDetails = {
              ...cropDetails,
              imageUrl: image,
            }
            setAvatar(imageDetails)
          }}
          open={editAvatarModal}
          toggleModal={() => setEditAvatarModal(!editAvatarModal)}
          circularCrop
        />
      )}
      <div className='payer-community-form__button-container'>
        <Button className='community_button--no-border' onClick={toggle}>
          {cancelButtonChild}
        </Button>
        <Button
          onClick={() => {
            handleSubmit(() => {
              if (!isCommunitySettings) {
                toggle()
              }
            })
          }}
          disabled={submitDisabled}
        >
          {submitButtonChild}
        </Button>
      </div>
    </div>
  )
}

export default withStores(
  [
    'communityMembersStore',
    'communityMemberStore',
    'userStore',
    'communitiesStore',
    'courseViewsStore',
    'communityPostsStore',
  ],
  PayerCommunityForm
)
